import { IPromise, IQService, ILocationService, copy } from "angular";

import { IAppConfig } from "@gtmhub/env";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { UserProfileService } from "@gtmhub/users/userProfile/profileService";
import { IAnalyticsPagePayload } from "./models";
import { Intercom } from "../../shared/intercom";
import { storage } from "@gtmhub/core/storage";
import { IChecklist } from "@gtmhub/onboarding";
import { AccountType, IAccount } from "@gtmhub/core";
import { LOG_TRACKING } from "@gtmhub/build";
import { getCurrentUserId } from "@gtmhub/users";
import { UIRouterGlobals } from "@uirouter/angularjs";
import { EffectivePermissionsService } from "@gtmhub/permissions/effective-permissions-service";

export type TrackingMetadata = Record<string, unknown>;

declare const analytics: SegmentAnalytics.AnalyticsJS;

export interface IAnalyticsPageOptions {
  path?: string;
  controller?: string;
}

export class AnalyticsService {
  public static $inject = ["$q", "$location", "AccountResolverService", "UserProfileService", "appConfig", "$uiRouterGlobals", "EffectivePermissionsService"];

  constructor(
    private $q: IQService,
    private $location: ILocationService,
    private accountResolverService: AccountResolverService,
    private profileService: UserProfileService,
    private appConfig: IAppConfig,
    private router: UIRouterGlobals,
    private effectivePermissions: EffectivePermissionsService
  ) {}

  public track(event: string, meta?: TrackingMetadata): IPromise<void> {
    try {
      if (LOG_TRACKING) {
        const analyticsPayload = this.generateAnalyticsTrackPayload(meta);
        const analyticsOptions = this.generateAnalyticsOptions();
        // eslint-disable-next-line no-console
        console.log("ANALYTICS: EVENT", event, analyticsPayload, analyticsOptions);
      }

      if (!this.useAnalytics(`Track: ${event}`)) {
        return this.$q.resolve();
      }

      const analyticsPayload = this.generateAnalyticsTrackPayload(meta);
      const analyticsOptions = this.generateAnalyticsOptions();
      analytics.track(event, analyticsPayload, analyticsOptions);

      return this.$q.resolve();
    } catch (error) {
      console.error(error);
      return this.$q.resolve();
    }
  }

  public page(page: string, opts?: IAnalyticsPageOptions): IPromise<void> {
    try {
      if (LOG_TRACKING) {
        const analyticsPageProperties = this.generateAnalyticsPagePayload(opts);
        const analyticsOptions = this.generateAnalyticsOptions();
        // eslint-disable-next-line no-console
        console.log("ANALYTICS: PAGE", page, analyticsPageProperties, analyticsOptions);
      }

      if (!this.useAnalytics(`Page: ${page}`)) {
        return this.$q.resolve();
      }

      const analyticsPageProperties = this.generateAnalyticsPagePayload(opts);
      const analyticsOptions = this.generateAnalyticsOptions();
      analytics.page(page, analyticsPageProperties, analyticsOptions);
    } catch (error) {
      console.error(error);
    }

    return this.$q.resolve();
  }

  public setOnboardingProperties(onboardingChecklist: IChecklist): void {
    try {
      if (!this.useAnalytics("setOnboardingProperties.analytics.identify")) {
        return;
      }

      analytics.identify(getCurrentUserId(), onboardingChecklist);
    } catch (error) {
      console.error(error);
    }
  }

  public identify(): void {
    try {
      if (!this.useAnalytics("analytics.identify")) {
        return;
      }
      this.effectivePermissions.initEffectivePermissions().then((perms: string[]) => {
        const accountData = this.accountResolverService.getAccountData();
        let accountStatus = "Unknown";
        switch (accountData.type) {
          case 0:
            accountStatus = "Trial";
            break;
          case 1:
            accountStatus = "Active";
            break;
          case 2:
            accountStatus = "Internal";
            break;
          case 3:
            accountStatus = "Demo";
            break;
          //partner type should also be marked as active
          case 4:
            accountStatus = "Active";
            break;
          case 5:
            accountStatus = "Free";
            break;
          default:
            accountStatus = "Internal";
        }

        const userInfo = {
          id: getCurrentUserId(),
          email: this.profileService.getProfile().email,
          name: this.profileService.getProfile().firstName + " " + this.profileService.getProfile().lastName,
          firstName: this.profileService.getProfile().firstName,
          lastName: this.profileService.getProfile().lastName,
          roles: storage.get<string[]>("roles") || [],
          permissions: perms,
          created: this.profileService.getProfile().created,
          picture: this.profileService.getProfile().picture,
        };

        let isPrimary = this.profileService.getProfile().isPrimary;
        if (isPrimary === undefined) {
          isPrimary = true;
        }

        const integrations = this.generateAnalyticsIntegrations(accountData);

        analytics.identify(
          userInfo.id,
          {
            email: userInfo.email,
            name: userInfo.name,
            firstName: userInfo.firstName,
            lastName: userInfo.lastName,
            company: {
              id: accountData.id,
              name: accountData.name,
              createdAt: accountData.dateCreated,
              plan: accountData.edition.planId,
              accountStatus: accountStatus,
              edition: accountData.edition.name,
            },
            avatar: userInfo.picture,
            createdAt: userInfo.created,
            roles: userInfo.roles.join(","),
            permissions: userInfo.permissions.join(","),
            accountId: accountData.id,
            accountName: accountData.name,
            accountstatus: accountStatus,
            accountcreated: accountData.dateCreated,
            trialends: accountData.trialEnds,
            editionname: accountData.edition.name,
            editionplanid: accountData.edition.planId,
            isPrimary: isPrimary,
            screenSize: `${screen.width}x${screen.height}`,
            screenWidth: screen.width,
            screenHeight: screen.height,
          },
          {
            integrations,
          }
        );
      });
    } catch (error) {
      console.error(error);
    }
  }

  public reset(): void {
    try {
      if (!this.useAnalytics("analytics.reset")) {
        return;
      }

      analytics.reset();
    } catch (error) {
      console.error(error);
    }
  }

  public contactSales(eventName: string): void {
    Intercom("showNewMessage");

    const meta = {
      account_id: this.accountResolverService.getAccountId(),
      page: window.location.hash,
    };
    this.track(eventName, meta);
  }

  private shouldTrackSegment(eventName: string): boolean {
    const account = this.accountResolverService.getAccountData();
    if (!account) {
      console.error(`No account data supplied, analytics disabled. ${eventName}`);
      return false;
    }

    return account.trackingProperties.shouldTrack;
  }

  private useAnalytics(eventName: string): boolean {
    return this.appConfig.segment.enabled && this.shouldTrackSegment(eventName);
  }

  private generateAnalyticsPagePayload(opts: IAnalyticsPageOptions = {}): IAnalyticsPagePayload {
    const path = opts.path || this.$location.url();
    const hash = this.$location.hash();

    return {
      controller: opts.controller,
      location_hash: hash,
      path,
      gtmhub_application_name: "webapp",
      gtmhub_path: this.router.current.name,
    };
  }

  private generateAnalyticsOptions(): SegmentAnalytics.SegmentOpts {
    const account = this.accountResolverService.getAccountData();
    if (!account) {
      return {};
    }
    const integrations = this.generateAnalyticsIntegrations(account);

    return {
      context: {
        groupId: account.id,
        accountType: account.type,
      },
      integrations,
    };
  }

  private generateAnalyticsIntegrations(account: IAccount) {
    const integrations = {};
    if (account.type === AccountType.DemoAccount) {
      integrations["Intercom"] = false;
    }
    return integrations;
  }

  private generateAnalyticsTrackPayload(meta: TrackingMetadata = {}) {
    const payload = copy(meta);

    if (!("accountId" in payload) && !("account_id" in payload)) {
      payload.account_id = this.accountResolverService.getAccountId();
    }

    payload.gtmhub_application_name = "webapp";
    payload.gtmhub_path = this.router.current.name;

    return payload;
  }
}
